import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './FAQ.css';
import { FaBook, FaBriefcase, FaEnvelope, FaFileAlt, FaInfoCircle, FaMinus, FaPlus, FaSearch, FaTools, FaUserCircle } from 'react-icons/fa';

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState([]);

  // FAQ categories with their respective questions and answers
  const faqData = {
    general: [
      {
        id: 'gen-1',
        question: "What are CVance and OppLiy?",
        answer: "CVance is a resume tailoring platform that optimizes your existing resume for specific job descriptions without altering your core information. It creates ATS-friendly versions that highlight your relevant skills and experiences. OppLiy is our job listing platform that aggregates job postings from across the United States to help you find relevant opportunities in one place."
      },
      {
        id: 'gen-2',
        question: "Is this service free to use?",
        answer: "Yes, CVance and OppLiy are currently available to use free of charge. We're in the project development phase and are offering these tools as a non-profit service to help job seekers streamline their application process."
      },
      {
        id: 'gen-3',
        question: "Do I need to create an account to use CVance and OppLiy?",
        answer: "Yes, a user account is required to access both platforms. This allows us to save your profile information, tailored resumes, and job search preferences for a seamless experience."
      },
      {
        id: 'gen-4',
        question: "Is my data secure on your platform?",
        answer: "We take data security seriously and implement appropriate measures to protect your information. Your resume data and personal details are only used for the purpose of tailoring your resume and providing job recommendations. We never share your information with third parties without your consent."
      },
      {
        id: 'gen-5',
        question: "What devices and browsers are supported?",
        answer: "CVance and OppLiy are web-based applications that work on desktop and mobile devices. For optimal performance, we recommend using the latest versions of Chrome, Firefox, Safari, or Edge. The resume tailoring features work best on desktop or tablet devices due to the detailed nature of the process."
      }
    ],
    cvance: [
      {
        id: 'cv-1',
        question: "How does CVance tailor my resume?",
        answer: "CVance analyzes the job description you provide to identify key requirements, skills, and qualifications. It then reorganizes and optimizes your resume content (without changing your actual information) to highlight the most relevant experiences and skills for that specific job, making your resume more likely to pass through Applicant Tracking Systems (ATS)."
      },
      {
        id: 'cv-2',
        question: "Will CVance change the content of my resume?",
        answer: "No, CVance does not alter your core information or add fabricated details. It only optimizes the presentation and organization of your existing information to highlight what's most relevant to the job you're applying for. You always have final approval over the tailored resume before downloading it."
      },
      {
        id: 'cv-3',
        question: "What file formats are supported for resume uploads?",
        answer: "CVance supports the following file formats for resume uploads: PDF (.pdf), Microsoft Word (.docx, .doc), Plain Text (.txt), and Rich Text Format (.rtf). The maximum file size is 5MB."
      },
      {
        id: 'cv-4',
        question: "How long does it take to tailor a resume?",
        answer: "The tailoring process typically takes just a few minutes. After pasting the job description and selecting which profile elements to include, our system will generate an optimized version of your resume that you can review and download."
      },
      {
        id: 'cv-5',
        question: "What is ATS and why is it important?",
        answer: "ATS (Applicant Tracking System) is software used by employers to scan, sort, and rank job applications. Many companies use ATS to filter out resumes before they reach human recruiters. CVance ensures your resume is ATS-friendly by incorporating relevant keywords and formatting that these systems can easily parse, increasing your chances of making it past these automated filters."
      },
      {
        id: 'cv-6',
        question: "Can I edit the tailored resume before downloading?",
        answer: "Yes, you have the option to review and make adjustments to your tailored resume before downloading it. While we recommend minimal changes to preserve the optimization for ATS systems, you have final control over the content."
      }
    ],
    oppliy: [
      {
        id: 'op-1',
        question: "How does OppLiy find job listings?",
        answer: "OppLiy aggregates job postings from various job boards, company career pages, and other sources across the United States. Our platform continuously updates to provide you with the most current opportunities that match your skills and preferences."
      },
      {
        id: 'op-2',
        question: "Can I filter job searches by location?",
        answer: "Yes, OppLiy allows you to filter job searches by location, including city, state, or remote options. You can also set a radius from your preferred location to include nearby opportunities."
      },
      {
        id: 'op-3',
        question: "How often are new jobs added to OppLiy?",
        answer: "OppLiy is updated regularly throughout the day to add new job postings as they become available. The frequency of updates ensures you have access to the most current opportunities."
      },
      {
        id: 'op-4',
        question: "Can I save job listings for later?",
        answer: "Yes, you can save job listings to your favorites for easy access later. This feature helps you keep track of opportunities you're interested in while continuing your search."
      },
      {
        id: 'op-5',
        question: "How do I set up job alerts?",
        answer: "To set up job alerts, perform a search with your desired criteria, then click the 'Save Search' button. You'll have the option to receive email notifications when new jobs matching your criteria are posted. You can choose daily, weekly, or 'as posted' notification frequency."
      }
    ],
    profile: [
      {
        id: 'prof-1',
        question: "What should I include in my profile?",
        answer: "For best results, your profile should include your education history, work experience, skills, projects, certifications, and any other relevant information. The more complete your profile, the better our system can tailor your resume for specific job opportunities."
      },
      {
        id: 'prof-2',
        question: "Can I have multiple versions of my resume in my profile?",
        answer: "Yes, CVance allows you to save multiple tailored versions of your resume in your profile. This is especially useful if you're applying to different types of positions or industries."
      },
      {
        id: 'prof-3',
        question: "How do I update my profile information?",
        answer: "To update your profile, navigate to the Profile section from the dashboard. You'll find separate tabs for each section (Personal Info, Education, Skills, Experience, etc.) with options to add, edit, or remove entries as needed."
      },
      {
        id: 'prof-4',
        question: "Is there a limit to how many experiences, skills, or education entries I can add?",
        answer: "No, there's no limit to the number of entries you can add to your profile. However, when tailoring your resume, you'll have the option to select which entries to include, as it's generally recommended to keep resumes concise and relevant to the specific job."
      }
    ],
    technical: [
      {
        id: 'tech-1',
        question: "What should I do if I can't upload my resume?",
        answer: "If you're having trouble uploading your resume, please ensure: 1) Your file is in a supported format (PDF, DOCX, DOC, TXT), 2) The file size is under 5MB, 3) You have a stable internet connection. If problems persist, try converting your file to PDF format or manually entering your information into your profile."
      },
      {
        id: 'tech-2',
        question: "Why isn't my tailored resume showing all my information?",
        answer: "This could happen if: 1) Your profile is incomplete, 2) You didn't select all relevant profile sections during the tailoring process, or 3) Some information wasn't deemed relevant to the job description. You can always edit the tailored resume to add any missing information you feel is important."
      },
      {
        id: 'tech-3',
        question: "What if I forgot my password?",
        answer: "If you forgot your password, click on the 'Forgot Password' link on the login page. You'll receive an email with instructions to reset your password. Be sure to check your spam folder if you don't see the email in your inbox."
      },
      {
        id: 'tech-4',
        question: "Can I use CVance and OppLiy on my mobile device?",
        answer: "Yes, both platforms are responsive and work on mobile devices. However, for the best experience with resume tailoring in CVance, we recommend using a desktop or tablet due to the detailed nature of the process. OppLiy works well on all devices for job searching."
      },
      {
        id: 'tech-5',
        question: "What if I find a bug or have a suggestion for improvement?",
        answer: "We welcome your feedback! If you encounter a bug or have suggestions for improving our platforms, please use the Contact Us page to report the issue or share your ideas. Your input helps us enhance the user experience."
      }
    ]
  };

  // Toggle FAQ item expansion
  const toggleItem = (id) => {
    setExpandedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id) 
        : [...prev, id]
    );
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter FAQs based on search term
  const getFilteredFaqs = () => {
    if (!searchTerm.trim()) {
      return faqData[activeCategory];
    }
    
    const searchTermLower = searchTerm.toLowerCase();
    
    // Search across all categories
    const allFaqs = Object.values(faqData).flat();
    
    return allFaqs.filter(faq => 
      faq.question.toLowerCase().includes(searchTermLower) || 
      faq.answer.toLowerCase().includes(searchTermLower)
    );
  };

  const filteredFaqs = getFilteredFaqs();

  return (
    <div className="faq-page">
      {/* SVG Definitions for Patterns */}
      <svg 
        width="0" 
        height="0" 
        style={{ position: 'absolute' }}
      >
        <defs>
          <pattern 
            id="gridPattern" 
            width="20" 
            height="20" 
            patternUnits="userSpaceOnUse"
          >
            <path 
              d="M 20 0 L 0 0 0 20" 
              fill="none" 
              stroke="white" 
              strokeWidth="0.5" 
              opacity="0.3"
            />
          </pattern>
        </defs>
      </svg>

      <div className="faq-hero">
        <div className="faq-hero__overlay">
          <svg 
            width="100%" 
            height="100%" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect 
              width="100%" 
              height="100%" 
              fill="url(#gridPattern)" 
            />
          </svg>
        </div>
        <div className="faq-container">
          <h1 className="faq-hero__title">Frequently Asked Questions</h1>
          <p className="faq-hero__subtitle">Find answers to common questions about CVance & OppLiy</p>
          
          <div className="faq-search">
            <input 
              type="text" 
              placeholder="Search FAQ..." 
              className="faq-search__input"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <button className="faq-search__button">
              <FaSearch />
            </button>
          </div>
        </div>
      </div>

      <div className="faq-container">
        <div className="faq-content">
          <div className="faq-categories">
            <button 
              className={`faq-category ${activeCategory === 'general' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('general')}
            >
              <FaInfoCircle /> General
            </button>
            <button 
              className={`faq-category ${activeCategory === 'cvance' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('cvance')}
            >
              <FaFileAlt /> CVance
            </button>
            <button 
              className={`faq-category ${activeCategory === 'oppliy' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('oppliy')}
            >
              <FaBriefcase /> OppLiy
            </button>
            <button 
              className={`faq-category ${activeCategory === 'profile' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('profile')}
            >
              <FaUserCircle /> Profile
            </button>
            <button 
              className={`faq-category ${activeCategory === 'technical' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('technical')}
            >
              <FaTools /> Technical
            </button>
          </div>

          <div className="faq-list">
            {searchTerm && (
              <div className="faq-search-results">
                <p className="faq-search-results__text">
                  {filteredFaqs.length > 0 
                    ? `Showing ${filteredFaqs.length} results for "${searchTerm}"`
                    : `No results found for "${searchTerm}"`
                  }
                </p>
              </div>
            )}
            
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map(faq => (
                <div 
                  key={faq.id} 
                  className={`faq-item ${expandedItems.includes(faq.id) ? 'expanded' : ''}`}
                >
                  <div 
                    className="faq-question"
                    onClick={() => toggleItem(faq.id)}
                  >
                    <span>{faq.question}</span>
                    <span className="faq-toggle-icon">
                      {expandedItems.includes(faq.id) ? 
                        <FaMinus /> : 
                        <FaPlus />
                      }
                    </span>
                  </div>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="faq-empty">
                <div className="faq-empty__icon">
                  <FaSearch />
                </div>
                <h3 className="faq-empty__title">No Matching Questions Found</h3>
                <p className="faq-empty__text">
                  Try adjusting your search terms or browse the categories.
                </p>
              </div>
            )}
          </div>
        </div>
        
        <div className="faq-contact">
          <div className="faq-contact__content">
            <h2 className="faq-contact__title">Still Have Questions?</h2>
            <p className="faq-contact__text">
              Can't find what you're looking for? Our support team is ready to help you with any questions or issues.
            </p>
            <div className="faq-contact__buttons">
              <Link to="/help" className="faq-button faq-button--secondary">
                <FaBook /> Visit Help Center
              </Link>
              <Link to="/contact" className="faq-button faq-button--primary">
                <FaEnvelope /> Contact Support
              </Link>
            </div>
          </div>
        </div>
        
        <section className="faq-disclaimer">
          <div className="disclaimer-content">
            <h3 className="disclaimer-title">Project Status Disclaimer</h3>
            <p className="disclaimer-text">
              CVance and OppLiy are currently in the project development phase. We are not a registered 
              company or LLC at this time. This is a non-profit project aimed at addressing challenges 
              in the job search process. The platforms and services described are under development and 
              subject to change. Any information provided is for informational purposes only and does not 
              constitute a commercial offering or binding agreement.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FAQ;